import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import analytics from '../services/AnalyticsService';

/**
 * Custom hook for Klorah Analytics
 * Provides easy access to analytics functions and automatic page tracking
 */
export const useAnalytics = () => {
  const location = useLocation();

  // Track page views automatically when route changes
  useEffect(() => {
    analytics.trackPageView({
      page: location.pathname,
      search: location.search,
      hash: location.hash
    });
  }, [location]);

  // Memoized analytics functions
  const identify = useCallback((userId, properties) => {
    return analytics.identify(userId, properties);
  }, []);

  const track = useCallback((eventName, properties) => {
    return analytics.track(eventName, properties);
  }, []);

  const trackSignup = useCallback((signupData) => {
    return analytics.trackSignup(signupData);
  }, []);

  const trackOnboarding = useCallback((stage, stepData) => {
    return analytics.trackOnboarding(stage, stepData);
  }, []);

  const trackSubscription = useCallback((subscriptionData) => {
    return analytics.trackSubscription(subscriptionData);
  }, []);

  // Photography business specific tracking functions
  const trackInvoiceCreated = useCallback((invoiceData) => {
    return analytics.trackInvoiceCreated(invoiceData);
  }, []);

  const trackInvoiceSent = useCallback((invoiceData) => {
    return analytics.trackInvoiceSent(invoiceData);
  }, []);

  const trackPaymentReceived = useCallback((paymentData) => {
    return analytics.trackPaymentReceived(paymentData);
  }, []);

  const trackContractCreated = useCallback((contractData) => {
    return analytics.trackContractCreated(contractData);
  }, []);

  const trackContractSigned = useCallback((contractData) => {
    return analytics.trackContractSigned(contractData);
  }, []);

  const trackClientAdded = useCallback((clientData) => {
    return analytics.trackClientAdded(clientData);
  }, []);

  const trackPhotoUploaded = useCallback((photoData) => {
    return analytics.trackPhotoUploaded(photoData);
  }, []);

  const trackGalleryShared = useCallback((galleryData) => {
    return analytics.trackGalleryShared(galleryData);
  }, []);

  const trackEmailSent = useCallback((emailData) => {
    return analytics.trackEmailSent(emailData);
  }, []);

  const trackFeatureUsed = useCallback((featureName, featureData) => {
    return analytics.trackFeatureUsed(featureName, featureData);
  }, []);

  const trackProjectCreated = useCallback((projectData) => {
    return analytics.trackProjectCreated(projectData);
  }, []);

  const trackReportGenerated = useCallback((reportData) => {
    return analytics.trackReportGenerated(reportData);
  }, []);

  return {
    // Core functions
    identify,
    track,
    trackSignup,
    trackOnboarding,
    trackSubscription,
    
    // Business specific functions
    trackInvoiceCreated,
    trackInvoiceSent,
    trackPaymentReceived,
    trackContractCreated,
    trackContractSigned,
    trackClientAdded,
    trackPhotoUploaded,
    trackGalleryShared,
    trackEmailSent,
    trackFeatureUsed,
    trackProjectCreated,
    trackReportGenerated,
    
    // Utility functions
    disable: analytics.disable.bind(analytics),
    enable: analytics.enable.bind(analytics),
    reset: analytics.reset.bind(analytics)
  };
};

export default useAnalytics; 